// frontend/features/hr/hrThunks.js
import { createAsyncThunk } from "@reduxjs/toolkit";

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Igual que en chats
const toList = (data) => (Array.isArray(data) ? data : (data?.results ?? []));

/**
 * Perfil de empleado del usuario logueado
 * GET /hr/me/
 */
export const fetchMyEmployeeProfile = createAsyncThunk(
  "hr/fetchMyEmployeeProfile",
  async (_, thunkAPI) => {
    const { auth } = thunkAPI.getState();
    const token = auth?.token;
    if (!token) {
      return thunkAPI.rejectWithValue("No se encontró token de autenticación.");
    }

    const uri = `${API_BASE_URL}/hr/me/`;
    const options = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };

    try {
      const response = await fetch(uri, options);
      if (!response.ok) {
        throw new Error("No se pudo cargar el perfil de empleado.");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

/**
 * Lista de empleados (panel manager)
 * GET /hr/employees/
 */
export const fetchEmployeesList = createAsyncThunk(
  "hr/fetchEmployeesList",
  async (filters, thunkAPI) => {
    const { auth } = thunkAPI.getState();
    const token = auth?.token;
    if (!token) {
      return thunkAPI.rejectWithValue("No se encontró token de autenticación.");
    }

    let uri = `${API_BASE_URL}/hr/employees/`;
    const params = [];
    if (filters?.department) {
      params.push(`department=${encodeURIComponent(filters.department)}`);
    }
    if (filters?.status) {
      params.push(`status=${encodeURIComponent(filters.status)}`);
    }
    if (params.length > 0) {
      uri += `?${params.join("&")}`;
    }

    const options = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };

    try {
      const response = await fetch(uri, options);
      if (!response.ok) {
        throw new Error("No se pudo cargar la lista de empleados.");
      }
      const data = await response.json();
      return toList(data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const updateEmployeeProfile = createAsyncThunk(
  "hr/updateEmployeeProfile",
  async ({ employeeId, payload }, thunkAPI) => {
    const { auth } = thunkAPI.getState();
    const token = auth?.token;
    if (!token) {
      return thunkAPI.rejectWithValue("No se encontró token de autenticación.");
    }

    const uri = `${API_BASE_URL}/hr/employees/${employeeId}/`;
    const options = {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload || {}),
    };

    try {
      const response = await fetch(uri, options);
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(
          (data && (data.detail || data.non_field_errors)) ||
            "No se pudieron actualizar los datos del empleado."
        );
      }
      return data; // EmployeeProfile actualizado
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

/**
 * Fichar entrada
 * POST /hr/time-entries/check-in/
 */
export const checkInTimeEntry = createAsyncThunk(
  "hr/checkInTimeEntry",
  async (_, thunkAPI) => {
    const { auth } = thunkAPI.getState();
    const token = auth?.token;
    if (!token) {
      return thunkAPI.rejectWithValue("No se encontró token de autenticación.");
    }

    const uri = `${API_BASE_URL}/hr/time-entries/check-in/`;
    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({}),
    };

    try {
      const response = await fetch(uri, options);
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.detail || "No se pudo fichar la entrada.");
      }
      return data; // TimeEntry
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

/**
 * Fichar salida
 * POST /hr/time-entries/check-out/
 */
export const checkOutTimeEntry = createAsyncThunk(
  "hr/checkOutTimeEntry",
  async (_, thunkAPI) => {
    const { auth } = thunkAPI.getState();
    const token = auth?.token;
    if (!token) {
      return thunkAPI.rejectWithValue("No se encontró token de autenticación.");
    }

    const uri = `${API_BASE_URL}/hr/time-entries/check-out/`;
    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({}),
    };

    try {
      const response = await fetch(uri, options);
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.detail || "No se pudo fichar la salida.");
      }
      return data; // TimeEntry
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

/**
 * Mis fichajes
 * GET /hr/time-entries/my/?start_date=&end_date=
 */
export const fetchMyTimeEntries = createAsyncThunk(
  "hr/fetchMyTimeEntries",
  async (filters, thunkAPI) => {
    const { auth } = thunkAPI.getState();
    const token = auth?.token;
    if (!token) {
      return thunkAPI.rejectWithValue("No se encontró token de autenticación.");
    }

    let uri = `${API_BASE_URL}/hr/time-entries/my/`;
    const params = [];
    if (filters?.startDate) {
      params.push(`start_date=${encodeURIComponent(filters.startDate)}`);
    }
    if (filters?.endDate) {
      params.push(`end_date=${encodeURIComponent(filters.endDate)}`);
    }
    if (params.length > 0) {
      uri += `?${params.join("&")}`;
    }

    const options = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };

    try {
      const response = await fetch(uri, options);
      if (!response.ok) {
        throw new Error("No se pudieron cargar tus fichajes.");
      }
      const data = await response.json();
      return toList(data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

/**
 * Fichajes para manager
 * GET /hr/time-entries/manager/?employee_id=&start_date=&end_date=
 */
export const fetchManagerTimeEntries = createAsyncThunk(
  "hr/fetchManagerTimeEntries",
  async (filters, thunkAPI) => {
    const { auth } = thunkAPI.getState();
    const token = auth?.token;
    if (!token) {
      return thunkAPI.rejectWithValue("No se encontró token de autenticación.");
    }

    let uri = `${API_BASE_URL}/hr/time-entries/manager/`;
    const params = [];
    if (filters?.employeeId) {
      params.push(`employee_id=${encodeURIComponent(filters.employeeId)}`);
    }
    if (filters?.startDate) {
      params.push(`start_date=${encodeURIComponent(filters.startDate)}`);
    }
    if (filters?.endDate) {
      params.push(`end_date=${encodeURIComponent(filters.endDate)}`);
    }
    if (params.length > 0) {
      uri += `?${params.join("&")}`;
    }

    const options = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };

    try {
      const response = await fetch(uri, options);
      if (!response.ok) {
        throw new Error("No se pudieron cargar los fichajes (manager).");
      }
      const data = await response.json();
      return toList(data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

/**
 * Mis solicitudes de ausencias
 * GET /hr/leave-requests/my/?status=
 */
export const fetchMyLeaveRequests = createAsyncThunk(
  "hr/fetchMyLeaveRequests",
  async (filters, thunkAPI) => {
    const { auth } = thunkAPI.getState();
    const token = auth?.token;
    if (!token) {
      return thunkAPI.rejectWithValue("No se encontró token de autenticación.");
    }

    let uri = `${API_BASE_URL}/hr/leave-requests/my/`;
    const params = [];
    if (filters?.status) {
      params.push(`status=${encodeURIComponent(filters.status)}`);
    }
    if (params.length > 0) {
      uri += `?${params.join("&")}`;
    }

    const options = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };

    try {
      const response = await fetch(uri, options);
      if (!response.ok) {
        throw new Error("No se pudieron cargar tus solicitudes de ausencia.");
      }
      const data = await response.json();
      return toList(data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

/**
 * Crear solicitud de ausencia
 * POST /hr/leave-requests/create/
 */
export const createLeaveRequest = createAsyncThunk(
  "hr/createLeaveRequest",
  async (payload, thunkAPI) => {
    const { auth } = thunkAPI.getState();
    const token = auth?.token;
    if (!token) {
      return thunkAPI.rejectWithValue("No se encontró token de autenticación.");
    }

    const uri = `${API_BASE_URL}/hr/leave-requests/create/`;
    const bodyPayload = {
      leave_type: payload.leaveType,
      start_date: payload.startDate,
      end_date: payload.endDate,
      reason: payload.reason || "",
    };

    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(bodyPayload),
    };

    try {
      const response = await fetch(uri, options);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data?.detail ||
            data?.non_field_errors ||
            "No se pudo crear la solicitud de ausencia."
        );
      }

      return data; // LeaveRequest
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

/**
 * Crear solicitud de ausencia como manager (para un empleado concreto)
 * POST /hr/leave-requests/manager/create/
 */
export const createManagerLeaveRequest = createAsyncThunk(
  "hr/createManagerLeaveRequest",
  async (payload, thunkAPI) => {
    const { auth } = thunkAPI.getState();
    const token = auth?.token;
    if (!token) {
      return thunkAPI.rejectWithValue(
        "No se encontró token de autenticación."
      );
    }

    const uri = `${API_BASE_URL}/hr/leave-requests/manager/create/`;
    const bodyPayload = {
      employee_id: payload.employeeId, // 👈 empleado elegido por el manager
      leave_type: payload.leaveType,
      start_date: payload.startDate,
      end_date: payload.endDate,
      reason: payload.reason || "",
    };

    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(bodyPayload),
    };

    try {
      const response = await fetch(uri, options);
      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(
          data?.detail ||
            data?.non_field_errors ||
            "No se pudo crear la ausencia del empleado."
        );
      }

      // devolvemos la LeaveRequest creada
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

/**
 * Solicitudes de ausencias (manager)
 * GET /hr/leave-requests/manager/?status=&employee_id=
 */
export const fetchManagerLeaveRequests = createAsyncThunk(
  "hr/fetchManagerLeaveRequests",
  async (filters, thunkAPI) => {
    const { auth } = thunkAPI.getState();
    const token = auth?.token;
    if (!token) {
      return thunkAPI.rejectWithValue("No se encontró token de autenticación.");
    }

    let uri = `${API_BASE_URL}/hr/leave-requests/manager/`;
    const params = [];
    if (filters?.status) {
      params.push(`status=${encodeURIComponent(filters.status)}`);
    }
    if (filters?.employeeId) {
      params.push(`employee_id=${encodeURIComponent(filters.employeeId)}`);
    }
    if (params.length > 0) {
      uri += `?${params.join("&")}`;
    }

    const options = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };

    try {
      const response = await fetch(uri, options);
      if (!response.ok) {
        throw new Error(
          "No se pudieron cargar las solicitudes de ausencia (manager)."
        );
      }
      const data = await response.json();
      return toList(data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

/**
 * Aprobar solicitud de ausencia
 * POST /hr/leave-requests/<id>/approve/
 */
export const approveLeaveRequest = createAsyncThunk(
  "hr/approveLeaveRequest",
  async ({ leaveRequestId, managerComment }, thunkAPI) => {
    const { auth } = thunkAPI.getState();
    const token = auth?.token;
    if (!token) {
      return thunkAPI.rejectWithValue("No se encontró token de autenticación.");
    }

    const uri = `${API_BASE_URL}/hr/leave-requests/${leaveRequestId}/approve/`;
    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        manager_comment: managerComment || "",
      }),
    };

    try {
      const response = await fetch(uri, options);
      const data = await response.json();
      if (!response.ok) {
        throw new Error(
          data?.detail || "No se pudo aprobar la solicitud de ausencia."
        );
      }
      return data; // LeaveRequest actualizado
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

/**
 * Rechazar solicitud de ausencia
 * POST /hr/leave-requests/<id>/reject/
 */
export const rejectLeaveRequest = createAsyncThunk(
  "hr/rejectLeaveRequest",
  async ({ leaveRequestId, managerComment }, thunkAPI) => {
    const { auth } = thunkAPI.getState();
    const token = auth?.token;
    if (!token) {
      return thunkAPI.rejectWithValue("No se encontró token de autenticación.");
    }

    const uri = `${API_BASE_URL}/hr/leave-requests/${leaveRequestId}/reject/`;
    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        manager_comment: managerComment || "",
      }),
    };

    try {
      const response = await fetch(uri, options);
      const data = await response.json();
      if (!response.ok) {
        throw new Error(
          data?.detail || "No se pudo rechazar la solicitud de ausencia."
        );
      }
      return data; // LeaveRequest actualizado
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

/**
 * Actualizar solicitud de ausencia (manager)
 * PATCH /hr/leave-requests/<id>/
 */
export const updateManagerLeaveRequest = createAsyncThunk(
  "hr/updateManagerLeaveRequest",
  async ({ leaveRequestId, payload }, thunkAPI) => {
    const { auth } = thunkAPI.getState();
    const token = auth?.token;
    if (!token) {
      return thunkAPI.rejectWithValue("No se encontró token de autenticación.");
    }

    const uri = `${API_BASE_URL}/hr/leave-requests/${leaveRequestId}/`;
    const options = {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload || {}),
    };

    try {
      const response = await fetch(uri, options);
      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(
          data?.detail ||
            data?.non_field_errors ||
            "No se pudo actualizar la solicitud de ausencia."
        );
      }

      return data; // LeaveRequest actualizado
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

/**
 * Eliminar solicitud de ausencia (manager)
 * DELETE /hr/leave-requests/<id>/
 */
export const deleteManagerLeaveRequest = createAsyncThunk(
  "hr/deleteManagerLeaveRequest",
  async (leaveRequestId, thunkAPI) => {
    const { auth } = thunkAPI.getState();
    const token = auth?.token;
    if (!token) {
      return thunkAPI.rejectWithValue("No se encontró token de autenticación.");
    }

    const uri = `${API_BASE_URL}/hr/leave-requests/${leaveRequestId}/`;
    const options = {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };

    try {
      const response = await fetch(uri, options);

      if (!response.ok && response.status !== 204) {
        let data = null;
        try {
          data = await response.json();
        } catch (e) {}
        throw new Error(data?.detail || "No se pudo eliminar la solicitud de ausencia.");
      }

      return leaveRequestId;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

/**
 * Horario base de un empleado (manager)
 * GET /hr/work-schedule/<employee_id>/
 */
export const fetchWorkSchedule = createAsyncThunk(
  "hr/fetchWorkSchedule",
  async ({ employeeId }, thunkAPI) => {
    const { auth } = thunkAPI.getState();
    const token = auth?.token;
    if (!token) {
      return thunkAPI.rejectWithValue("No se encontró token de autenticación.");
    }

    const uri = `${API_BASE_URL}/hr/work-schedule/${employeeId}/`;
    const options = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };

    try {
      const response = await fetch(uri, options);
      if (!response.ok) {
        throw new Error("No se pudo cargar el horario base.");
      }
      const data = await response.json();
      return { employeeId, workSchedule: data };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

/**
 * Actualizar horario base de un empleado (manager)
 * PUT /hr/work-schedule/<employee_id>/
 */
export const updateWorkSchedule = createAsyncThunk(
  "hr/updateWorkSchedule",
  async ({ employeeId, scheduleData }, thunkAPI) => {
    const { auth } = thunkAPI.getState();
    const token = auth?.token;
    if (!token) {
      return thunkAPI.rejectWithValue("No se encontró token de autenticación.");
    }

    const uri = `${API_BASE_URL}/hr/work-schedule/${employeeId}/`;
    const options = {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(scheduleData || {}),
    };

    try {
      const response = await fetch(uri, options);
      if (!response.ok) {
        throw new Error("No se pudo actualizar el horario base.");
      }
      const data = await response.json();
      return { employeeId, workSchedule: data };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

/**
 * Resumen de vacaciones del empleado logueado
 * GET /hr/vacations/my-summary/?year=
 */
export const fetchMyVacationSummary = createAsyncThunk(
  "hr/fetchMyVacationSummary",
  async (payload, thunkAPI) => {
    const { auth } = thunkAPI.getState();
    const token = auth?.token;
    if (!token) {
      return thunkAPI.rejectWithValue("No se encontró token de autenticación.");
    }

    let uri = `${API_BASE_URL}/hr/vacations/my-summary/`;
    const params = [];
    if (payload?.year) {
      params.push(`year=${encodeURIComponent(payload.year)}`);
    }
    if (params.length > 0) {
      uri += `?${params.join("&")}`;
    }

    const options = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };

    try {
      const response = await fetch(uri, options);
      if (!response.ok) {
        throw new Error("No se pudo cargar el resumen de vacaciones.");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

/**
 * Calendario anual personal
 * GET /hr/calendar/my/?year=
 */
export const fetchMyCalendar = createAsyncThunk(
  "hr/fetchMyCalendar",
  async (payload, thunkAPI) => {
    const { auth } = thunkAPI.getState();
    const token = auth?.token;
    if (!token) {
      return thunkAPI.rejectWithValue("No se encontró token de autenticación.");
    }

    let uri = `${API_BASE_URL}/hr/calendar/my/`;
    const params = [];
    if (payload?.year) {
      params.push(`year=${encodeURIComponent(payload.year)}`);
    }
    if (params.length > 0) {
      uri += `?${params.join("&")}`;
    }

    const options = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };

    try {
      const response = await fetch(uri, options);
      if (!response.ok) {
        throw new Error("No se pudo cargar el calendario anual.");
      }
      const data = await response.json();
      return data; // { year, events: [...] }
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

/**
 * Reporte de trabajo para manager
 * GET /hr/reports/work-summary/?start_date=&end_date=
 */
export const fetchWorkSummaryReport = createAsyncThunk(
  "hr/fetchWorkSummaryReport",
  async (filters, thunkAPI) => {
    const { auth } = thunkAPI.getState();
    const token = auth?.token;
    if (!token) {
      return thunkAPI.rejectWithValue("No se encontró token de autenticación.");
    }

    let uri = `${API_BASE_URL}/hr/reports/work-summary/`;
    const params = [];
    if (filters?.startDate) {
      params.push(`start_date=${encodeURIComponent(filters.startDate)}`);
    }
    if (filters?.endDate) {
      params.push(`end_date=${encodeURIComponent(filters.endDate)}`);
    }
    if (params.length > 0) {
      uri += `?${params.join("&")}`;
    }

    const options = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };

    try {
      const response = await fetch(uri, options);
      if (!response.ok) {
        throw new Error("No se pudo cargar el reporte de trabajo.");
      }
      const data = await response.json();
      return data; // { start_date, end_date, employees: [...] }
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

/**
 * Actualizar fichaje (manager)
 * PATCH /hr/time-entries/<id>/
 */
export const updateManagerTimeEntry = createAsyncThunk(
  "hr/updateManagerTimeEntry",
  async ({ timeEntryId, payload }, thunkAPI) => {
    const { auth } = thunkAPI.getState();
    const token = auth?.token;
    if (!token) {
      return thunkAPI.rejectWithValue("No se encontró token de autenticación.");
    }

    const uri = `${API_BASE_URL}/hr/time-entries/${timeEntryId}/`;
    const options = {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload || {}),
    };

    try {
      const response = await fetch(uri, options);
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(
          (data && data.detail) || "No se pudo actualizar el fichaje."
        );
      }
      // devolvemos el fichaje actualizado
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

/**
 * Eliminar fichaje (manager)
 * DELETE /hr/time-entries/<id>/
 */
export const deleteManagerTimeEntry = createAsyncThunk(
  "hr/deleteManagerTimeEntry",
  async (timeEntryId, thunkAPI) => {
    const { auth } = thunkAPI.getState();
    const token = auth?.token;
    if (!token) {
      return thunkAPI.rejectWithValue("No se encontró token de autenticación.");
    }

    const uri = `${API_BASE_URL}/hr/time-entries/${timeEntryId}/`;
    const options = {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };

    try {
      const response = await fetch(uri, options);

      if (!response.ok && response.status !== 204) {
        let data = null;
        try {
          data = await response.json();
        } catch (e) {
          // ignoramos si no hay JSON
        }
        throw new Error(
          (data && data.detail) || "No se pudo eliminar el fichaje."
        );
      }

      // devolvemos solo el id borrado
      return timeEntryId;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const createEmployee = createAsyncThunk(
  "hr/createEmployee",
  async (payload, thunkAPI) => {
    const { auth } = thunkAPI.getState();
    const token = auth?.token;
    if (!token) return thunkAPI.rejectWithValue("No se encontró token de autenticación.");

    const uri = `${API_BASE_URL}/hr/employees/create/`;
    const bodyPayload = {
      email: payload.email,
      first_name: payload.firstName || "",
      last_name: payload.lastName || "",
      phone: payload.phone || "",
      role: payload.role, // admin | doctor | receptionist
      password: payload.password || "",

      position: payload.position || "",
      department: payload.department || "",
      employment_type: payload.employmentType || "full_time",
      weekly_hours: payload.weeklyHours !== "" ? Number(payload.weeklyHours) : 40,
      start_date: payload.startDate || null,
      end_date: payload.endDate || null,
      status: payload.status || "active",
      annual_vacation_days: payload.annualVacationDays !== "" ? Number(payload.annualVacationDays) : 22,
      manager_id: payload.managerId ? Number(payload.managerId) : null,
    };

    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(bodyPayload),
    };

    try {
      const response = await fetch(uri, options);
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(data?.detail || data?.non_field_errors || "No se pudo crear el empleado.");
      }
      return data; // EmployeeProfile
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const deleteEmployeeProfile = createAsyncThunk(
  "hr/deleteEmployeeProfile",
  async (employeeId, thunkAPI) => {
    const { auth } = thunkAPI.getState();
    const token = auth?.token;
    if (!token) {
      return thunkAPI.rejectWithValue("No se encontró token de autenticación.");
    }

    const uri = `${API_BASE_URL}/hr/employees/${employeeId}/`;
    const options = {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };

    try {
      const response = await fetch(uri, options);

      if (!response.ok && response.status !== 204) {
        let data = null;
        try {
          data = await response.json();
        } catch (e) {}
        throw new Error(data?.detail || "No se pudo eliminar el empleado.");
      }

      return employeeId;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);